import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import Header from "../components/dashboard/header";
import SideMenu from "./student/SideMenu";
import Footer from "../components/dashboard/footer";
import tmdbApi from "../api/tmdbApi";
import { ToastContainer, toast } from "react-toastify";

const CasesCreate = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [isDragging, setIsDragging] = useState(false);
    const [fileName, setFileName] = useState("");
    const [importData, setImportData] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);

    const isHtmlContent = (str) => /<\/?[a-z][\s\S]*>/i.test(str);

    const resolveCol = (row, ...keys) => {
        const normalizedRow = {};
        Object.keys(row).forEach((k) => {
            normalizedRow[k.trim().toLowerCase()] = row[k];
        });
        for (const key of keys) {
            const val = normalizedRow[key.trim().toLowerCase()];
            if (val !== undefined && val !== null && val !== "") return val;
        }
        return "";
    };

    const plainTextToHtml = (text) => {
        const trimmed = String(text).trim();
        if (!trimmed) return "";
        const paragraphs = trimmed
            .split(/\n+/)
            .map((p) => p.trim())
            .filter(Boolean);
        return paragraphs.map((p) => `<p>${p}</p>`).join("");
    };

    const processExcel = (file) => {
        if (!file) return;

        const ext = file.name.split(".").pop().toLowerCase();
        if (ext !== "xls" && ext !== "xlsx") {
            toast.error("Only Excel (.xls / .xlsx) files are allowed.");
            return;
        }

        setFileName(file.name);
        setImportData(null);
        setImportResult(null);

        const reader = new FileReader();
        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array", cellText: false, cellDates: true });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            const rawRows = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });

            console.log("📋 Raw Excel rows:", rawRows);

            const formattedData = rawRows
                .map((row) => {
                    const rawDiagnosisid = resolveCol(row, "Ref No", "RefNo", "ref no", "refno", "diagnosisid", "DiagnosisId");
                    const rawDiagnosis = resolveCol(row, "Diagnosis", "diagnosis");
                    const rawClinicalhistory = resolveCol(row, "Clinical History", "ClinicalHistory", "clinical history", "clinicalhistory");
                    const rawExplanation = resolveCol(row, "Histopathological findings", "Histopathological Findings", "histopathological findings", "explanation", "Explanation");

                    const diagnosisid = String(rawDiagnosisid).trim();
                    const diagnosis = String(rawDiagnosis).trim();
                    const clinicalhistory = (() => {
                        const val = String(rawClinicalhistory).trim();
                        if (!val) return "";
                        return isHtmlContent(val) ? val : plainTextToHtml(val);
                    })();
                    const explanation = (() => {
                        const val = String(rawExplanation).trim();
                        if (!val) return "";
                        return isHtmlContent(val) ? val : plainTextToHtml(val);
                    })();

                    return { clinicalhistory, diagnosis, diagnosisid, explanation };
                })
                .filter((row) => row.diagnosisid || row.clinicalhistory || row.explanation);

            const payload = { data: formattedData };
            console.log("✅ Formatted payload to be sent:", JSON.stringify(payload, null, 2));
            console.log(`📦 Total records ready to create: ${formattedData.length}`);

            setImportData(payload);
        };
        reader.readAsArrayBuffer(file);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) processExcel(file);
        e.target.value = "";
    };

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) processExcel(file);
    }, []);

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleImport = async () => {
        if (!importData) return;
        setIsImporting(true);
        console.log("🚀 Sending create payload to API:", JSON.stringify(importData, null, 2));
        try {
            const response = await tmdbApi.createCases(importData);
            console.log("📥 API response (unwrapped):", response);

            if (response === "Invalid token or Expired" || response?.result === "Invalid token or Expired") {
                toast.error("Session expired. Please log in again.");
                return;
            }

            const result =
                response?.result && typeof response.result === "object" && "total" in response.result
                    ? response.result
                    : response;

            console.log("📊 Create result summary:", result);
            console.log(`📈 inserted=${result?.inserted}, created=${result?.created}, rejected=${result?.rejected}, total=${result?.total}`);

            setImportResult(result);

            // Support both `inserted` and `created` field names from the API
            const created  = Number(result?.inserted ?? result?.created ?? 0);
            const rejected = Number(result?.rejected ?? 0);
            const total    = Number(result?.total    ?? 0);

            if (created > 0 && rejected === 0) {
                toast.success(`${created} case${created !== 1 ? "s" : ""} created successfully!`);
            } else if (created > 0 && rejected > 0) {
                toast.warning(`${created} created, ${rejected} failed out of ${total}.`);
            } else {
                toast.error(`No cases could be created. All ${rejected} record${rejected !== 1 ? "s" : ""} failed.`);
            }

        } catch (err) {
            console.error("❌ Create error:", err);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setIsImporting(false);
        }
    };

    const handleRemoveFile = () => {
        setFileName("");
        setImportData(null);
        setImportResult(null);
    };

    const handleImportAnother = () => {
        handleRemoveFile();
        setTimeout(() => fileInputRef.current.click(), 50);
    };

    // ── Result summary card ──
    const ResultCard = ({ result }) => {
        // Support both `inserted` and `created` field names
        const { total = 0, rejected = 0, rejectedData = [] } = result || {};
        const created = Number(result?.inserted ?? result?.created ?? 0);

        const allSuccess = created > 0 && rejected === 0;
        const allFailed  = created === 0 && rejected > 0;

        const headerColor  = allSuccess ? "#2e7d32" : allFailed ? "#b71c1c" : "#e65100";
        const headerBg     = allSuccess ? "#f1f8f1"  : allFailed ? "#fff5f5"  : "#fff8f0";
        const headerBorder = allSuccess ? "#c8e6c9"  : allFailed ? "#ffcdd2"  : "#ffe0b2";
        const headerIcon   = allSuccess ? "mdi-check-circle" : allFailed ? "mdi-close-circle" : "mdi-alert-circle";
        const headerText   = allSuccess
            ? "Cases Created Successfully"
            : allFailed
            ? "No Cases Could Be Created"
            : "Import Completed with Some Failures";
        const headerSub = allFailed
            ? "None of the records in your Excel could be created. Please check the data and try again."
            : !allSuccess
            ? "Some records could not be created. See the table below for details."
            : null;

        const stats = [
            { label: "Total",   value: total,    icon: "mdi-database",           bg: "#e3f2fd", color: "#1565c0", iconColor: "#1976d2" },
            { label: "Created", value: created,  icon: "mdi-plus-circle-outline", bg: "#e8f5e9", color: "#1b5e20", iconColor: "#2e7d32" },
            {
                label: "Failed", value: rejected, icon: "mdi-close-circle-outline",
                bg: rejected > 0 ? "#ffebee" : "#f5f5f5",
                color: rejected > 0 ? "#c62828" : "#757575",
                iconColor: rejected > 0 ? "#e53935" : "#bdbdbd",
            },
        ];

        return (
            <div>
                {/* Header banner */}
                <div
                    style={{
                        borderRadius: "14px",
                        border: `1.5px solid ${headerBorder}`,
                        backgroundColor: headerBg,
                        padding: "18px 20px",
                        marginBottom: "18px",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "14px",
                        textAlign: "left",
                    }}
                >
                    <i className={`mdi ${headerIcon}`} style={{ fontSize: "30px", color: headerColor, flexShrink: 0, marginTop: "2px" }} />
                    <div>
                        <p className="mb-0 fw-semibold" style={{ fontSize: "15px", color: headerColor }}>{headerText}</p>
                        {headerSub && (
                            <p className="mb-0 mt-1" style={{ fontSize: "12px", color: "#666" }}>{headerSub}</p>
                        )}
                    </div>
                </div>

                {/* Stats row */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px", marginBottom: "20px" }}>
                    {stats.map((s) => (
                        <div
                            key={s.label}
                            style={{
                                borderRadius: "12px",
                                backgroundColor: s.bg,
                                padding: "14px 12px",
                                textAlign: "center",
                            }}
                        >
                            <i className={`mdi ${s.icon}`} style={{ fontSize: "24px", color: s.iconColor, display: "block" }} />
                            <p className="mb-0 mt-1" style={{ fontSize: "22px", fontWeight: "700", color: s.color, lineHeight: 1 }}>
                                {s.value}
                            </p>
                            <p className="mb-0 mt-1" style={{ fontSize: "11px", color: "#666", fontWeight: "500" }}>
                                {s.label}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Failed records table */}
                {rejectedData.length > 0 && (
                    <div style={{ marginBottom: "22px", textAlign: "left" }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                marginBottom: "10px",
                            }}
                        >
                            <i className="mdi mdi-alert-circle-outline" style={{ fontSize: "18px", color: "#c62828" }} />
                            <p className="mb-0 fw-semibold" style={{ fontSize: "14px", color: "#c62828" }}>
                                Failed Records — {rejectedData.length} case{rejectedData.length !== 1 ? "s" : ""} could not be created
                            </p>
                        </div>
                        <div style={{ border: "1px solid #ffcdd2", borderRadius: "10px", overflow: "hidden" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                <thead>
                                    <tr style={{ backgroundColor: "#ffebee" }}>
                                        <th style={{ padding: "10px 14px", fontWeight: "600", color: "#b71c1c", width: "40px", borderBottom: "1px solid #ffcdd2" }}>
                                            #
                                        </th>
                                        <th style={{ padding: "10px 14px", fontWeight: "600", color: "#b71c1c", borderBottom: "1px solid #ffcdd2" }}>
                                            Ref No / Diagnosis ID
                                        </th>
                                        <th style={{ padding: "10px 14px", fontWeight: "600", color: "#b71c1c", borderBottom: "1px solid #ffcdd2" }}>
                                            Reason
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rejectedData.map((item, idx) => (
                                        <tr
                                            key={idx}
                                            style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#fff5f5" }}
                                        >
                                            <td style={{ padding: "10px 14px", color: "#999", borderBottom: idx < rejectedData.length - 1 ? "1px solid #fce4e4" : "none" }}>
                                                {idx + 1}
                                            </td>
                                            <td style={{ padding: "10px 14px", fontWeight: "500", color: "#333", borderBottom: idx < rejectedData.length - 1 ? "1px solid #fce4e4" : "none" }}>
                                                {item.diagnosisid ? (
                                                    <span style={{
                                                        display: "inline-block",
                                                        padding: "2px 10px",
                                                        borderRadius: "12px",
                                                        backgroundColor: "#f3e5f5",
                                                        color: "#6a1b9a",
                                                        fontSize: "12px",
                                                        fontWeight: "600",
                                                    }}>
                                                        {item.diagnosisid}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: "#aaa", fontStyle: "italic", fontSize: "12px" }}>— not provided —</span>
                                                )}
                                            </td>
                                            <td style={{ padding: "10px 14px", color: "#c62828", borderBottom: idx < rejectedData.length - 1 ? "1px solid #fce4e4" : "none" }}>
                                                <i className="mdi mdi-alert-circle-outline me-1" style={{ fontSize: "13px" }} />
                                                {item.reason || "Unknown error"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Action buttons */}
                <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                    <button
                        className="btn btn-primarys create-quiz-btn px-4"
                        onClick={() => navigate("/cases")}
                        style={{ minWidth: "160px" }}
                    >
                        <i className="mdi mdi-view-list me-1" />
                        View Cases
                    </button>
                    <button
                        className="btn btn-primarys create-quiz-btn px-4"
                        onClick={handleImportAnother}
                        style={{ minWidth: "160px" }}
                    >
                        <i className="mdi mdi-file-import me-1" />
                        Import Another
                    </button>
                </div>
            </div>
        );
    };

    return (
        <>
            <div id="layout-wrapper">
                <Header />
                <SideMenu />

                <div className="main-content" id="main-content">
                    <div className="page-content" id="page-content">
                        <div className="container-fluid" id="container-fluid">

                            {/* Breadcrumb */}
                            <div className="row mb-4 breadcrumb">
                                <div className="col-lg-12">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <h4 className="mb-2 card-title">CREATE CASES</h4>
                                        </div>
                                        <div>
                                            <a onClick={() => navigate("/cases")} className="stroke-btn-style create-quiz-btn" style={{ cursor: "pointer" }}>
                                                back
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Main card */}
                            <div className="row justify-content-center">
                                <div className="col-lg-7 col-md-10 col-12">
                                    <div className="card shadow-sm">
                                        <div className="card-body p-5 text-center">

                                            {/* ── State 1: Create result ── */}
                                            {importResult ? (
                                                <ResultCard result={importResult} />
                                            ) : !fileName ? (
                                                /* ── State 2: Drag & drop zone ── */
                                                <div
                                                    onDrop={handleDrop}
                                                    onDragOver={handleDragOver}
                                                    onDragLeave={handleDragLeave}
                                                    onClick={() => fileInputRef.current.click()}
                                                    style={{
                                                        border: `2px dashed ${isDragging ? "var(--secondaryColor, #6c6cff)" : "#d0d0d0"}`,
                                                        borderRadius: "16px",
                                                        padding: "60px 30px",
                                                        cursor: "pointer",
                                                        backgroundColor: isDragging ? "#f0f0ff" : "#fafafa",
                                                        transition: "all 0.2s ease",
                                                    }}
                                                >
                                                    <i
                                                        className="mdi mdi-file-excel-outline"
                                                        style={{
                                                            fontSize: "72px",
                                                            color: isDragging ? "var(--secondaryColor, #6c6cff)" : "#b0b0b0",
                                                            display: "block",
                                                        }}
                                                    />
                                                    <p className="mt-3 mb-1 fw-semibold" style={{ fontSize: "16px", color: "#444" }}>
                                                        Click the icon to select a file or drag &amp; drop
                                                    </p>
                                                    <p className="mb-0" style={{ fontSize: "13px", color: "#999" }}>
                                                        Only Excel (.xls / .xlsx) Files Allowed
                                                    </p>
                                                </div>
                                            ) : (
                                                /* ── State 3: File selected ── */
                                                <div>
                                                    <div
                                                        style={{
                                                            border: "1.5px solid #c3e6cb",
                                                            borderRadius: "16px",
                                                            padding: "36px 24px",
                                                            backgroundColor: "#f6fff8",
                                                        }}
                                                    >
                                                        {/* Excel icon circle */}
                                                        <div
                                                            style={{
                                                                width: "72px",
                                                                height: "72px",
                                                                borderRadius: "50%",
                                                                backgroundColor: "#e6f4ea",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                margin: "0 auto 18px",
                                                            }}
                                                        >
                                                            <i className="mdi mdi-file-excel" style={{ fontSize: "36px", color: "#2e7d32" }} />
                                                        </div>

                                                        {/* File name */}
                                                        <p className="fw-semibold mb-1" style={{ fontSize: "15px", color: "#2e7d32", wordBreak: "break-all" }}>
                                                            {fileName}
                                                        </p>

                                                        {/* Record count badge */}
                                                        {importData && (
                                                            <span
                                                                style={{
                                                                    display: "inline-block",
                                                                    marginTop: "8px",
                                                                    padding: "4px 14px",
                                                                    borderRadius: "20px",
                                                                    backgroundColor: "#d4edda",
                                                                    color: "#155724",
                                                                    fontSize: "13px",
                                                                    fontWeight: "600",
                                                                }}
                                                            >
                                                                {importData.data.length} record{importData.data.length !== 1 ? "s" : ""} ready to create
                                                            </span>
                                                        )}

                                                        {/* Change file link */}
                                                        <div className="mt-3">
                                                            <span
                                                                onClick={() => { handleRemoveFile(); setTimeout(() => fileInputRef.current.click(), 50); }}
                                                                style={{ fontSize: "13px", color: "#888", cursor: "pointer", textDecoration: "underline" }}
                                                            >
                                                                Choose a different file
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Create button */}
                                                    {importData && (
                                                        <button
                                                            className="btn btn-primarys create-quiz-btn mt-4 px-5"
                                                            onClick={handleImport}
                                                            disabled={isImporting}
                                                            style={{ minWidth: "220px" }}
                                                        >
                                                            {isImporting ? (
                                                                <>
                                                                    <span className="spinner-border spinner-border-sm me-2" />
                                                                    Creating Cases...
                                                                </>
                                                            ) : (
                                                                "Click Now to Create Cases"
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            )}

                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept=".xls,.xlsx"
                                                style={{ display: "none" }}
                                                onChange={handleFileChange}
                                            />

                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>

                    <Footer />
                </div>
            </div>

            <ToastContainer
                position="bottom-center"
                autoClose={4000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
                draggable
            />
        </>
    );
};

export default CasesCreate;
